import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import { Theme } from '@seedlang/constants';
import { DeckUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import { flexCenter } from '@seedlang/style_mixins';
import { LearnedIcon, CheckWithCircleIcon } from '@seedlang/icons';
import TranslationWithSource from 'components/translation_with_source';
import SelectionButton from 'components/button/selection_button';

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 0 10px;
  background: ${props => props.background};
  text-align: left;
  position: relative;
  justify-content: flex-end;
  width: 100%;
  min-height: 40px;
  align-items: center;
  .inner-row {
    display: flex;
    width: 100%;
  }
  .already-added {
    font-size: 12px;
    font-style: italic;
    color: ${Theme.gray};
  }
`;

const TranslationWrapper = styled.div`
  flex: 1;
  font-weight: ${props => props.fontWeight};
  font-size: 14px;
  display: flex;
`;

const Result = styled.div`
  ${flexCenter()}
  width: 30px;
  .fa-check {
    color: ${Theme.green};
  }
  .fa-times {
    color: ${Theme.red};
  }
`;

const Learned = styled.div`
  ${flexCenter()}
`;

const ButtonWrapper = styled.div`
  ${flexCenter()}
  width: 120px;
  .bookmark-icon {
    svg {
      width: 14px;
    }
    #background {
      fill: ${Theme.blue};
    }
    #border {
      fill: #FFF;
    }
    margin-right: 3px;
  }
  .star-with-circle-icon {
    fill: #FFF;
    margin-right: 3px;
  }
`;

const Reviewing = styled.div`
  ${flexCenter()}
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  .icon-wrapper {
    display: flex;
    align-items: center;
    margin-right: 5px;
  }
  .check-with-circle-icon svg {
    fill: ${Theme.green};
    width: 18px;
    height: 18px;
  }
`;

@observer
class DeckSummaryRow extends React.Component {

  get synonymAssociation() {
    return this.props.card.word?.synonymAssociations?.find(item => item.id === this.props.card.synonymAssociationId);
  }

  get synonymPairText() {
    if (this.props.card.cardType?.slug !== 'synonym' || !this.synonymAssociation?.synonym) {
      return null;
    }
    const wordText = this.props.card.word?.targetTextWithDefiniteArticle || this.props.card.word?.targetText;
    const synonymText = this.synonymAssociation.synonym.targetTextWithDefiniteArticle || this.synonymAssociation.synonym.targetText;
    return `${wordText} / ${synonymText}`;
  }

  get isSynonymCard() {
    return this.props.card.cardType?.slug === 'synonym';
  }

  get translation() {
    if (!this.isSynonymCard || !isPresent(this.synonymPairText)) {
      return this.props.deckSummaryCard.props.summaryTranslationForCard(this.props.card);
    }
    return {
      ...this.props.card.word,
      targetText: this.synonymPairText,
      targetTextWithDefiniteArticle: this.synonymPairText,
      sourceText: this.props.card.word.sourceText,
      sourceTextForUser: user => this.props.card.word.sourceTextForUser(user),
      sourceTextForUserLanguageId: user => this.props.card.word.sourceTextForUserLanguageId(user),
    };
  }

  render() {
    return (
      <Row>
        <div className='inner-row'>
          <TranslationWrapper>
            <TranslationWithSource
              flex='none'
              translation={this.translation}
              languageButtonWrapperWidth='18px'
              user={DeckUI.user}
            />
          </TranslationWrapper>
          {
            (this.props.card.cardType?.slug === 'gender' || this.props.card.cardType?.slug === 'plural' || this.props.card.cardType?.slug === 'conjugation' || this.props.card.cardType?.slug === 'case_multiple_choice' || this.props.card.cardType?.slug === 'case_fill_in_the_blanks') &&
            <Result>
              {this.props.deckSummaryCard.props.userCardForId(this.props.card.id).firstAnswerCorrect && <i className='fa fa-check' />}
              {!this.props.deckSummaryCard.props.userCardForId(this.props.card.id).firstAnswerCorrect && <i className='fa fa-times' />}
            </Result>
          }
          {
            ['vocab_word_translate', 'gender', 'plural', 'conjugation', 'synonym', 'number_options', 'number_translate', 'case_multiple_choice', 'case_fill_in_the_blanks'].includes(this.props.card.cardType?.slug) && this.props.card.word &&
            <Learned>
              <LearnedIcon
                showTooltip
                width='28px'
                id={this.props.card.word.id}
                selected={this.props.deckSummaryCard.props.isLearned(this.props.card.word.id)}
                onClick={() => this.props.deckSummaryCard.props.onToggleLearned(this.props.card.word.id)}
              />
            </Learned>
          }
          <ButtonWrapper>
            {
              !this.props.reviewCard &&
              <SelectionButton
                width='110px'
                icon={<i className='fa fa-plus' />}
                onClick={() => DeckUI.onToggleReview(this.props.card, this.props.deckSummaryCard.props.deckId)}
                background={Theme.blue}
                color='white'
                border='none'
              >
                Review
              </SelectionButton>
            }
            {
              this.props.reviewCard &&
              <Reviewing
                onClick={() => DeckUI.onToggleReview(this.props.card, this.props.deckSummaryCard.props.deckId)}
              >
                <CheckWithCircleIcon />
                Reviewing
              </Reviewing>
            }
          </ButtonWrapper>
        </div>
        {
          isPresent(this.props.decksWithReviewCard) &&
          <div
            className='already-added'
          >
            {isPresent(this.props.reviewCard) ? 'Also' : 'Already'} added to {this.props.decksWithReviewCard}.
          </div>
        }
      </Row>
    );
  }
}

export default DeckSummaryRow;

